import { Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../home/components/Footer';

export default function TierComparisonPage() {
  const [selectedTier, setSelectedTier] = useState<string>('tier3');
  const [selectedAddons, setSelectedAddons] = useState<string[]>([]);

  // Base prices by tier
  const tierPrices: Record<string, number> = {
    tier1: 299,
    tier2: 499,
    tier3: 899,
    tier4: 1499,
    tier5: 2499,
  };

  // Add-on prices
  const addonPrices: Record<string, number> = {
    'credit-score': 199,
    'app-passport': 299,
    'competitor-benchmark': 249,
    'certification': 299,
    'trend-retention': 199,
  };

  const tierOptions = [
    { 
      id: 'tier1', 
      name: 'Tier 1 – Quick Pulse™', 
      price: 299,
      description: 'Fast directional check with one key insight.'
    },
    { 
      id: 'tier2', 
      name: 'Tier 2 – Market Readiness™', 
      price: 499,
      description: 'Market fit check and risk scan before you invest more.'
    },
    { 
      id: 'tier3', 
      name: 'Tier 3 – Competitive Intelligence™', 
      price: 899,
      description: 'Deeper breakdown of your true position against competitors.'
    },
    { 
      id: 'tier4', 
      name: 'Tier 4 – Success Blueprint™', 
      price: 1499,
      description: 'Strategic roadmap with probability scoring and growth moves.'
    },
    { 
      id: 'tier5', 
      name: 'Tier 5 – Full Success Intelligence™', 
      price: 2499,
      description: 'All 7 intelligence systems combined for investor-grade clarity.'
    },
  ];

  const addons = [
    { 
      id: 'credit-score', 
      label: 'CrowdRounds App Credit Score™',
      description: 'A clear, standardized score that shows your app\'s overall performance and traction.',
      price: 199
    },
    { 
      id: 'app-passport', 
      label: 'App Passport™ & Long-Term Tracking',
      description: 'Ongoing tracking of key indicators so you can show progress over time.',
      price: 299
    },
    { 
      id: 'competitor-benchmark', 
      label: 'Competitor Benchmark Report',
      description: 'Side-by-side comparison against top apps in your category.',
      price: 249
    },
    { 
      id: 'certification', 
      label: 'Certification & Investor-Ready Summary',
      description: 'Concise certification and summary you can use in investor and partner conversations.',
      price: 299
    },
    { 
      id: 'trend-retention', 
      label: 'Trend & Retention Improvement Insights',
      description: 'Focused recommendations to improve engagement, retention, and growth.',
      price: 199
    },
  ];

  const totalInvestment = useMemo(() => {
    const basePrice = tierPrices[selectedTier];
    const addonsTotal = selectedAddons.reduce((sum, addonId) => {
      return sum + (addonPrices[addonId] || 0);
    }, 0);
    return basePrice + addonsTotal;
  }, [selectedTier, selectedAddons]);

  const handleAddonToggle = (addonId: string) => {
    setSelectedAddons(prev =>
      prev.includes(addonId)
        ? prev.filter(id => id !== addonId)
        : [...prev, addonId]
    );
  };

  const tiers = [
    {
      id: 'tier1',
      name: 'Quick Pulse™',
      tier: 'Tier 1',
      price: '$299',
      color: 'from-blue-500 to-cyan-500',
      link: '/tier1-quick-pulse'
    },
    {
      id: 'tier2',
      name: 'Market Readiness™',
      tier: 'Tier 2',
      price: '$499',
      color: 'from-teal-500 to-emerald-500',
      link: '/tier2-market-readiness'
    },
    {
      id: 'tier3',
      name: 'Competitive Intelligence™',
      tier: 'Tier 3',
      price: '$899',
      color: 'from-teal-600 to-blue-600',
      popular: true,
      link: '/tier3-competitive-intelligence'
    },
    {
      id: 'tier4',
      name: 'Success Blueprint™',
      tier: 'Tier 4',
      price: '$1,499',
      color: 'from-indigo-600 to-purple-600',
      link: '/tier4-success-blueprint'
    },
    {
      id: 'tier5',
      name: 'Full Success Intelligence™',
      tier: 'Tier 5',
      price: '$2,499',
      color: 'from-purple-600 to-pink-600',
      premium: true,
      link: '/tier5-full-success-intelligence'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Intelligence Investment Calculator
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Explore different intelligence tiers and add-ons, then see your estimated total in real time. This is for planning only—final pricing is confirmed at checkout.
          </p>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          {/* Calculator Card */}
          <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-100 p-8 md:p-12">
            {/* Step 1: Choose Tier */}
            <div className="mb-12">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                Step 1 — Choose your intelligence tier
              </h3>
              <div className="space-y-3">
                {tierOptions.map((option) => (
                  <label
                    key={option.id}
                    className="flex items-start p-4 border-2 rounded-xl cursor-pointer transition-all hover:border-teal-500 hover:bg-teal-50/50"
                    style={{
                      borderColor: selectedTier === option.id ? '#14B8A6' : '#E2E8F0',
                      backgroundColor: selectedTier === option.id ? '#F0FDFA' : 'transparent',
                    }}
                  >
                    <input
                      type="radio"
                      name="tier"
                      value={option.id}
                      checked={selectedTier === option.id}
                      onChange={(e) => setSelectedTier(e.target.value)}
                      className="w-5 h-5 text-teal-600 cursor-pointer mt-0.5 flex-shrink-0"
                    />
                    <div className="ml-4 flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-gray-900 font-semibold">{option.name}</span>
                        <span className="text-gray-900 font-bold">${option.price.toLocaleString()}</span>
                      </div>
                      <p className="text-sm text-gray-600">{option.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Step 2: Add-ons */}
            <div className="mb-12">
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                Step 2 — Add the upgrades you care about
              </h3>
              <div className="space-y-3">
                {addons.map((addon) => (
                  <label
                    key={addon.id}
                    className="flex items-start p-4 border-2 rounded-xl cursor-pointer transition-all hover:border-teal-500 hover:bg-teal-50/50"
                    style={{
                      borderColor: selectedAddons.includes(addon.id) ? '#14B8A6' : '#E2E8F0',
                      backgroundColor: selectedAddons.includes(addon.id) ? '#F0FDFA' : 'transparent',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedAddons.includes(addon.id)}
                      onChange={() => handleAddonToggle(addon.id)}
                      className="w-5 h-5 text-teal-600 rounded cursor-pointer mt-0.5 flex-shrink-0"
                    />
                    <div className="ml-4 flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-gray-900 font-semibold">{addon.label}</span>
                        <span className="text-teal-600 font-bold">+${addon.price}</span>
                      </div>
                      <p className="text-sm text-gray-600">{addon.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Step 3 - Total Investment */}
            <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-10 border-2 border-teal-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Step 3 — See your estimated investment
              </h3>
              
              <div className="bg-white rounded-xl p-8 mb-6">
                <p className="text-sm font-medium text-gray-600 mb-2">Your estimated investment:</p>
                <p className="text-5xl font-bold text-gray-900 mb-4">
                  ${totalInvestment.toLocaleString()}
                </p>
                <p className="text-sm text-gray-600 mb-6">
                  Based on your selected intelligence tier and optional upgrades.
                </p>
                
                {/* BNPL Messaging */}
                <div className="bg-gradient-to-r from-gray-50 to-white p-6 rounded-lg border border-gray-200 mb-6">
                  <p className="text-base font-semibold text-gray-900 mb-3">
                    💳 Payment Flexibility
                  </p>
                  <p className="text-sm text-gray-700 mb-4">
                    Break your investment into 4–12 monthly payments with flexible financing options. Immediate clarity, no financial barrier.
                  </p>
                  <p className="text-xs text-gray-500">
                    Buy now, pay later options available at checkout through Affirm, Klarna, or Afterpay.
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-teal-500 text-xl mt-0.5"></i>
                    <p className="text-sm text-gray-700">No long-term contracts</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-teal-500 text-xl mt-0.5"></i>
                    <p className="text-sm text-gray-700">Upgrade to a higher tier later</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-teal-500 text-xl mt-0.5"></i>
                    <p className="text-sm text-gray-700">Buy now, pay later options may be available at checkout</p>
                  </div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <Link
                  to="/submit-app"
                  className="flex-1 inline-flex items-center justify-center px-8 py-4 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Start Round 1
                </Link>
                <Link
                  to="/contact"
                  className="flex-1 inline-flex items-center justify-center px-8 py-4 bg-white text-teal-600 border-2 border-teal-600 rounded-lg font-semibold hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Book a Walkthrough
                </Link>
              </div>

              {/* Disclaimer */}
              <p className="text-xs text-gray-500 text-center leading-relaxed">
                This calculator is for planning only. Final pricing is confirmed at checkout based on your app's complexity and selected options.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Tier Details Cards */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Explore Each Tier in Detail
            </h2>
            <p className="text-xl text-gray-600">
              Click to learn more about what's included
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {tiers.map((tier) => (
              <Link
                key={tier.id}
                to={tier.link}
                className={`block bg-white rounded-2xl p-6 border-2 ${
                  tier.popular ? 'border-teal-400 shadow-xl' : tier.premium ? 'border-purple-400 shadow-xl' : 'border-gray-200'
                } hover:shadow-2xl transition-all cursor-pointer`}
              >
                {tier.popular && (
                  <div className="text-center mb-4">
                    <span className="inline-block bg-teal-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      Popular
                    </span>
                  </div>
                )}
                {tier.premium && (
                  <div className="text-center mb-4">
                    <span className="inline-flex items-center gap-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      <i className="ri-vip-crown-fill"></i>
                      Premium
                    </span>
                  </div>
                )}
                
                <div className={`inline-block bg-gradient-to-r ${tier.color} text-white px-3 py-1 rounded-full text-xs font-semibold mb-3`}>
                  {tier.tier}
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                <div className="text-3xl font-bold text-gray-900 mb-4">{tier.price}</div>
                
                <div className="flex items-center justify-center gap-2 text-teal-600 font-semibold">
                  Learn More
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-8">
            Still Have Questions?
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Contact Us
            </Link>
            <Link
              to="/faq"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-teal-600 border-2 border-teal-600 rounded-lg font-semibold hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              View FAQ
            </Link>
          </div>
        </div>
      </section>

      {/* Who CrowdRounds Is Built For */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Who CrowdRounds Is Built For
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-user-star-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">First-time founders</h3>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-robot-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">AI & no-code app creators</h3>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-flask-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">MVP builders</h3>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-line-chart-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Apps struggling with traction</h3>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-presentation-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Founders preparing for investors</h3>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-rocket-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Teams launching new features</h3>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-bar-chart-box-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Scale-stage apps</h3>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-search-eye-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Apps needing competitive insight</h3>
            </div>
          </div>
        </div>
      </section>

      {/* What Every Evaluation Includes */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Every Evaluation Includes
            </h2>
            <p className="text-xl text-gray-600">
              No matter which tier you choose, you get real intelligence
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-xl p-6 border border-teal-200">
              <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-check-double-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Clear strengths & weaknesses</h3>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-xl p-6 border border-teal-200">
              <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-percent-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Success probability insights</h3>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-xl p-6 border border-teal-200">
              <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-alert-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Red flag & risk detection</h3>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-xl p-6 border border-teal-200">
              <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-compass-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Competitive distance score</h3>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-xl p-6 border border-teal-200">
              <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-target-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Market fit analysis</h3>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-xl p-6 border border-teal-200">
              <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-stack-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">7-system intelligence breakdown</h3>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-xl p-6 border border-teal-200">
              <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-map-pin-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Strategic next steps</h3>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-xl p-6 border border-teal-200">
              <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-dashboard-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Scoring dashboard</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Security & Transparency + Flexible Payment */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Security Card */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-gray-200">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Security & Transparency
              </h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mt-0.5"></i>
                  <p className="text-gray-700">No backend access required</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mt-0.5"></i>
                  <p className="text-gray-700">No credentials requested</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mt-0.5"></i>
                  <p className="text-gray-700">All evaluations handled securely</p>
                </div>
              </div>
            </div>

            {/* Payment Flexibility Card */}
            <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-8 shadow-lg border-2 border-teal-500">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-6">
                <i className="ri-bank-card-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">
                Flexible Payment Options
              </h3>
              <p className="text-white/90 text-lg mb-6">
                Buy Now, Pay Later available at checkout for all tiers.
              </p>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                <p className="text-white/80 text-sm">
                  Break your investment into manageable monthly payments. Get clarity now, pay over time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
